/**
 * API Client
 * Axios instance with interceptors for authentication and error handling
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  (import.meta.env.DEV ? '/api/v1' : 'http://localhost:3000/api/v1');

/**
 * Generic API response type helper
 * Use this to type API responses: ApiResponse<User>, ApiResponse<User[]>, etc.
 */
export type ApiResponse<T = unknown> = AxiosResponse<T>;

/**
 * Axios instance with default configuration
 * Typed as AxiosInstance for better IDE support
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

export const publicApiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/**
 * Request interceptor - add CSRF proof to cookie-authenticated mutations.
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const method = config.method?.toUpperCase() ?? 'GET';
    const csrfToken = localStorage.getItem('csrfToken');
    if (csrfToken && config.headers && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    // For FormData, remove Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error: Error) => {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  },
);

publicApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error: Error) => {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  },
);

/**
 * Track if we're currently refreshing the token
 */
let isRefreshing = false;

/**
 * Queue of failed requests waiting for token refresh
 */
interface FailedRequest {
  resolve: () => void;
  reject: (error: unknown) => void;
}
let failedQueue: FailedRequest[] = [];

/**
 * Process queued requests after token refresh
 */
const processQueue = (error: unknown = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });

  failedQueue = [];
};

/**
 * Refresh the access token
 */
const refreshAccessToken = async (): Promise<void> => {
  try {
    await axios.post(
      `${API_BASE_URL}/identity/auth/session/refresh`,
      {},
      {
        withCredentials: true,
        headers: { 'X-CSRF-Token': localStorage.getItem('csrfToken') ?? '' },
      },
    );
  } catch (error) {
    // Clear auth data on refresh failure
    localStorage.removeItem('csrfToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');

    throw error;
  }
};

/**
 * Response interceptor - Handle errors and token refresh
 * Preserves generic type information through the interceptor chain
 */
apiClient.interceptors.response.use(
  <T = unknown>(response: AxiosResponse<T>) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // In unit tests, avoid running auth refresh flows. Many component tests intentionally
    // mock API modules and do not provide auth tokens; refreshing here can cause noisy
    // unhandled rejections that mask real failures.
    const isTestEnv = typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'test';

    // Handle 401 Unauthorized - Token expired
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (isTestEnv) {
        return Promise.reject(error);
      }

      // Don't retry auth endpoints to avoid infinite loops
      if (originalRequest.url?.includes('/identity/auth/')) {
        return Promise.reject(error);
      }

      // Mark request as retried
      originalRequest._retry = true;

      // Queue before starting refresh — if refresh resolves first, processQueue would
      // otherwise run with an empty queue and leave this request hanging forever.
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: () => {
            resolve(apiClient(originalRequest));
          },
          reject: (err: unknown) => {
            reject(err instanceof Error ? err : new Error(String(err)));
          },
        });

        if (!isRefreshing) {
          isRefreshing = true;
          void refreshAccessToken()
            .then(() => {
              processQueue();
            })
            .catch(() => {
              // Abandon queued requests silently — the page is navigating away.
              // Rejecting the queue would propagate a 401 AxiosError to component
              // call sites that were never written to handle refresh failure.
              failedQueue = [];
              // Redirect to login on refresh failure
              window.location.href = '/login';
              // Do NOT re-throw: this chain is void-prefixed so throwing here
              // would create an unhandled promise rejection captured by Sentry.
            })
            .finally(() => {
              isRefreshing = false;
            });
        }
      });
    }

    // Handle 403 Forbidden - User lacks permission
    if (error.response?.status === 403) {
      // Don't redirect, let the UI handle it
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

/**
 * Helper to clear authentication data
 */
export const clearAuth = () => {
  localStorage.removeItem('csrfToken');
  localStorage.removeItem('user');
  localStorage.removeItem('tenant');
};
